<template>
    <div class="flex flex-col">
        <div :class="['flex items-center w-full h-12 cursor-pointer hover:bg-gray-100 transition-colors', showBottomBorder ? 'border-b border-gray-200' : '']" @click="onToggleOption">
            <span class="flex-1 flex items-center truncate" :style="indentStyle">
                <span class="inline-flex items-center mr-3">
                    <button v-if="hasChildren" type="button" class="inline-flex rounded-full p-2.5 hover:bg-gray-200 transition-colors" @click.stop="onToggleExpand">
                        <ExpandIcon color="black" :expand="isExpanded" class="w-3.5 h-3.5 min-w-3.5" />
                    </button>
                    <span v-else class="inline-flex rounded-full p-2.5">
                        <span class="inline-block w-3.5 h-3.5"></span>
                    </span>
                </span>
                <span class="font-medium text-gray-800 truncate">{{ option.name }}</span>
            </span>
            <span class="w-6.5 pr-4 flex justify-center">
                <CustomCheckbox :checked="isSelected" :indeterminate="isIndeterminate" @update:checked="onToggleOption"></CustomCheckbox>
            </span>
        </div>
        <ExpandCollapseTransition>
            <div v-if="hasChildren && isExpanded" class="flex flex-col">
                <ContentOptionsNode
                    v-for="(child, index) in option.children"
                    :key="child.id"
                    :option="child"
                    :level="level + 1"
                    :selected-ids="selectedIds"
                    :expanded-ids="expandedIds"
                    :indeterminate-ids="indeterminateIds"
                    :is-last="isLast && index === option.children.length - 1"
                    @toggle-option="emitToggleOption"
                    @toggle-expand="emitToggleExpand"
                ></ContentOptionsNode>
            </div>
        </ExpandCollapseTransition>
    </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import ExpandCollapseTransition from '@/components/common/ExpandCollapseTransition.vue';
import ExpandIcon from '@/components/icons/ExpandIcon.vue';
import CustomCheckbox from '@/components/ui/CustomCheckbox.vue';
import { ReportContentOption } from '@/types';

type ContentOptionNode = ReportContentOption & { children: ContentOptionNode[] };

export default defineComponent({
    name: 'ContentOptionsNode',
    components: { CustomCheckbox, ExpandCollapseTransition, ExpandIcon },
    props: {
        option: {
            type: Object as () => ContentOptionNode,
            required: true,
        },
        level: {
            type: Number,
            default: 0,
        },
        selectedIds: {
            type: Array as () => number[],
            default: () => [],
        },
        expandedIds: {
            type: Array as () => number[],
            default: () => [],
        },
        indeterminateIds: {
            type: Array as () => number[],
            default: () => [],
        },
        isLast: {
            type: Boolean,
            default: false,
        },
    },
    emits: ['toggle-option', 'toggle-expand'],
    computed: {
        isSelected(): boolean {
            return this.selectedIds.includes(this.option.id);
        },
        hasChildren(): boolean {
            return !!this.option.children.length;
        },
        isExpanded(): boolean {
            return this.expandedIds.includes(this.option.id);
        },
        isIndeterminate(): boolean {
            return this.indeterminateIds.includes(this.option.id);
        },
        showBottomBorder(): boolean {
            if (!this.isLast) return true;
            return this.hasChildren && this.isExpanded;
        },
        indentStyle() {
            return { paddingLeft: `${1 + this.level * 1.5}rem` };
        },
    },
    methods: {
        onToggleOption() {
            this.$emit('toggle-option', this.option);
        },
        onToggleExpand() {
            this.$emit('toggle-expand', this.option.id);
        },
        emitToggleOption(option: ContentOptionNode) {
            this.$emit('toggle-option', option);
        },
        emitToggleExpand(optionId: number) {
            this.$emit('toggle-expand', optionId);
        },
    },
});
</script>
